import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Card, Space, Button, Statistic, Select, Switch } from "antd";
import {
  PlusOutlined,
  SyncOutlined,
  BookOutlined,
  AppstoreOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useHolidayPackages from "../../../hooks/academics/useHolidayPackages";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import HolidayPackageTable from "../../../components/academics/holiday_packages/HolidayPackageTable";
import HolidayPackageFormModal from "../../../components/academics/holiday_packages/HolidayPackageFormModal";
import HolidayPackageDetailDrawer from "../../../components/academics/holiday_packages/HolidayPackageDetailDrawer";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const HolidayPackagesManagement = () => {
  const navigate = useNavigate();

  const {
    packages,
    loading,
    fetchPackages,
    fetchPackageDetail,
    createPackage,
    updatePackage,
    activatePackage,
    deactivatePackage,
    archivePackage,
    hardDeletePackage,
    uploadPackageFiles,
    deletePackageFile,
  } = useHolidayPackages();

  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);

  // Filters
  const [filterAcadyrID, setFilterAcadyrID] = useState(null);
  const [filterTermID, setFilterTermID] = useState(null);
  const [filterClassID, setFilterClassID] = useState(null);
  const [filterSubjectID, setFilterSubjectID] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [viewPackageId, setViewPackageId] = useState(null);

  const fetchBootstrapData = useCallback(async () => {
    const [classRes, subjectRes] = await Promise.all([
      Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.classes,
        null,
        navigate,
      ),
      Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.subject,
        null,
        navigate,
      ),
    ]);
    if (classRes?.ok)
      setClasses(
        Array.isArray(classRes.data)
          ? classRes.data
          : classRes.data?.results || [],
      );
    if (subjectRes?.ok)
      setSubjects(
        Array.isArray(subjectRes.data)
          ? subjectRes.data
          : subjectRes.data?.results || [],
      );
  }, [navigate]);

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
    fetchBootstrapData();
  }, [fetchAcademicYears, fetchTerms, fetchBootstrapData]);

  useEffect(() => {
    const params = {};
    if (filterAcadyrID) params.acadyrID = filterAcadyrID;
    if (filterTermID) params.termID = filterTermID;
    if (filterClassID) params.school_class = filterClassID;
    if (filterSubjectID) params.subjectID = filterSubjectID;
    if (showArchived) params.include_inactive = true;
    fetchPackages(params);
  }, [
    filterAcadyrID,
    filterTermID,
    filterClassID,
    filterSubjectID,
    showArchived,
    fetchPackages,
  ]);

  // Statistics
  const totalPackages = packages.length;
  const uniqueClasses = new Set(packages.map((p) => p.className)).size;
  const filteredTermCount = filterTermID ? packages.length : packages.length;

  const handleView = (record) => {
    setViewPackageId(record.id);
    setDrawerVisible(true);
  };

  const handleEdit = async (record) => {
    const detail = await fetchPackageDetail(record.id);
    if (detail) {
      setEditingPackage(detail);
      setModalVisible(true);
    }
  };

  const handleActivate = async (record) => {
    const ok = await activatePackage(record.id);
    if (ok) refresh();
  };

  const handleDeactivate = async (record) => {
    const ok = await deactivatePackage(record.id);
    if (ok) refresh();
  };

  const handleArchive = async (record) => {
    const ok = await archivePackage(record.id);
    if (ok) refresh();
  };

  const handleDelete = async (record) => {
    const ok = await hardDeletePackage(record.id);
    if (ok) refresh();
  };

  const handleSubmit = async (formData, id) => {
    setSaving(true);
    let result;
    if (id) {
      result = await updatePackage(id, formData);
    } else {
      result = await createPackage(formData);
    }
    setSaving(false);
    if (result) {
      setModalVisible(false);
      setEditingPackage(null);
      refresh();
    }
  };

  const handleDeleteFileInModal = async (packageId, fileId) => {
    setDeletingFileId(fileId);
    const ok = await deletePackageFile(packageId, fileId);
    setDeletingFileId(null);
    if (ok && editingPackage) {
      setEditingPackage((prev) => ({
        ...prev,
        files: prev.files.filter((f) => f.id !== fileId),
      }));
    }
  };

  const refresh = () => {
    const params = {};
    if (filterAcadyrID) params.acadyrID = filterAcadyrID;
    if (filterTermID) params.termID = filterTermID;
    if (filterClassID) params.school_class = filterClassID;
    if (filterSubjectID) params.subjectID = filterSubjectID;
    if (showArchived) params.include_inactive = true;
    fetchPackages(params);
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Total Packages"
              value={totalPackages}
              prefix={<BookOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Classes Covered"
              value={uniqueClasses}
              prefix={<AppstoreOutlined />}
              valueStyle={{ color: "#1677ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Current View"
              value={filteredTermCount}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Card */}
      <Card
        title="Holiday Packages"
        extra={
          <Space>
            <Button icon={<SyncOutlined />} onClick={refresh} loading={loading}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingPackage(null);
                setModalVisible(true);
              }}
            >
              New Package
            </Button>
          </Space>
        }
      >
        {/* Filters */}
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Academic Year"
              allowClear
              style={{ width: "100%" }}
              value={filterAcadyrID}
              onChange={setFilterAcadyrID}
              showSearch
              optionFilterProp="label"
            >
              {academicYears.map((y) => (
                <Select.Option key={y.id} value={y.id} label={y.acadyr}>
                  {y.acadyr}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Term"
              allowClear
              style={{ width: "100%" }}
              value={filterTermID}
              onChange={setFilterTermID}
              showSearch
              optionFilterProp="label"
            >
              {terms.map((t) => (
                <Select.Option key={t.id} value={t.id} label={t.name}>
                  {t.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Class"
              allowClear
              style={{ width: "100%" }}
              value={filterClassID}
              onChange={setFilterClassID}
              showSearch
              optionFilterProp="label"
            >
              {classes.map((c) => (
                <Select.Option key={c.id} value={c.id} label={c.name}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Subject"
              allowClear
              style={{ width: "100%" }}
              value={filterSubjectID}
              onChange={setFilterSubjectID}
              showSearch
              optionFilterProp="label"
            >
              {subjects.map((s) => (
                <Select.Option key={s.id} value={s.id} label={s.name}>
                  {s.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Space>
              <Switch
                checked={showArchived}
                onChange={setShowArchived}
                size="small"
              />
              <span style={{ fontSize: 13 }}>Show Inactive/Archived</span>
            </Space>
          </Col>
        </Row>

        <HolidayPackageTable
          packages={packages}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onActivate={handleActivate}
          onDeactivate={handleDeactivate}
          onArchive={handleArchive}
          onDelete={handleDelete}
        />
      </Card>

      <HolidayPackageFormModal
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setEditingPackage(null);
        }}
        onSubmit={handleSubmit}
        onDeleteFile={handleDeleteFileInModal}
        saving={saving}
        deletingFileId={deletingFileId}
        pkg={editingPackage}
        academicYears={academicYears}
        terms={terms}
        classes={classes}
        subjects={subjects}
      />

      <HolidayPackageDetailDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        packageId={viewPackageId}
        onFetchDetail={fetchPackageDetail}
        onDeleteFile={deletePackageFile}
        onUploadFiles={uploadPackageFiles}
      />
    </div>
  );
};

export default HolidayPackagesManagement;
